"use client"

import Link from "next/link"
import Image from "next/image"
import { ChevronRight } from "lucide-react"
import { useMemo } from "react"
import { useQuery } from '@tanstack/react-query'
import { apiGetAllNews } from '@/services/newsHome/api'

// Danh sách ảnh fallback khi không có dữ liệu từ API
const FALLBACK_IMAGES = [
    '/images/About/gt.png',
    '/images/About/gt1.png',
    '/images/About/gt2.png',
];

// Hằng số cấu hình ID loại bài viết giới thiệu
const ABOUT_ID = Number(process.env.NEXT_PUBLIC_ABOUT_US_NEWS_TYPE_ID);

export default function RewardsSection() {

    // Gọi API lấy danh sách bài viết theo loại
    const { data: newsData } = useQuery({
        queryKey: ['rewards-images-content'],
        queryFn: async () => {
            const res = await apiGetAllNews({
                keywords: '',
                newsTypeId: ABOUT_ID,
                isPublic: '',
                page: 1,
                pageSize: 50,
            });
            return res?.isOK ? res.result.items : [];
        },
        staleTime: 5 * 60 * 1000,
    });

    // Xử lý trích xuất danh sách ảnh từ nội dung bài viết
    const images = useMemo(() => {
        // Kiểm tra dữ liệu danh sách bài viết
        if (newsData && Array.isArray(newsData)) {

            // Tìm bài viết chứa danh sách giải thưởng
            const galleryPost = newsData.find((item: any) =>
                item.seoDescription === 'Giải thưởng'
            );

            // Trích xuất danh sách ảnh từ nội dung HTML
            if (galleryPost && galleryPost.content) {
                const regex = /<img[^>]+src="([^">]+)"/g;
                const matches = [];
                let match;

                // Lặp để lấy toàn bộ đường dẫn ảnh
                while ((match = regex.exec(galleryPost.content)) !== null) {
                    matches.push(match[1]);
                }

                // Trả về danh sách ảnh nếu có dữ liệu hợp lệ
                if (matches.length > 0) {
                    return matches;
                }
            }
        }

        // Trả về ảnh fallback khi không có dữ liệu hợp lệ
        return FALLBACK_IMAGES;
    }, [newsData]);

    return (
        <section className="mx-auto px-0 xl:px-8 pt-0 pb-0 xl:pb-0 2xl:pb-8 max-w-7xl 2xl:max-w-[1450px] min-[1920px]:max-w-full min-[1920px]:px-12">
            {/* Khối bao ngoài của section hiển thị giải thưởng */}
            <div
                className="relative w-full xl:w-[98%] 2xl:w-full mx-auto rounded-none xl:rounded-3xl overflow-hidden h-auto min-h-[850px] xl:min-h-0 xl:h-[500px] 2xl:h-[600px] min-[1920px]:h-[850px]"
            >
                {/* Khối hiển thị ảnh nền cho mobile */}
                <div className="absolute inset-0 block xl:hidden">
                    <Image
                        src="/images/About/bg.png"
                        alt="Background Mobile"
                        fill
                        className="object-cover object-center z-0"
                    />
                </div>

                {/* Khối hiển thị ảnh nền cho desktop */}
                <div className="absolute inset-0 hidden xl:block">
                    <Image
                        src="/images/About/Section.png"
                        alt="Background Section"
                        fill
                        className="object-contain 2xl:object-cover object-center z-0"
                    />
                </div>

                {/* Khối bao nội dung hiển thị trên nền */}
                <div className="relative z-20 px-6 pt-20 pb-12 xl:px-12 xl:py-12 2xl:px-16 min-[1920px]:px-32 h-full flex flex-col justify-start xl:justify-center">

                    {/* Khối căn chỉnh layout nội dung */}
                    <div className="w-full xl:w-2/3 xl:ml-auto xl:pl-12 2xl:pl-20 min-[1920px]:pl-48 text-center xl:text-left">

                        {/* Khối tiêu đề và mô tả */}
                        <div className="grid grid-cols-1 gap-6 items-center mb-8 xl:mb-6 2xl:mb-8">
                            <div className="space-y-4 xl:space-y-4 2xl:space-y-5 flex flex-col items-center xl:items-start">
                                <div>
                                    <h2 className="text-2xl xl:text-4xl 2xl:text-5xl min-[1920px]:text-7xl font-normal text-white mb-3 2xl:mb-4 text-pretty tracking-wide">
                                        Giải thưởng nổi bật
                                    </h2>

                                    <p className="text-[13px] xl:text-[13px] 2xl:text-[15px] min-[1920px]:text-[20px] text-gray-300 font-normal leading-relaxed max-w-[500px] 2xl:max-w-[650px] tracking-wide mx-auto xl:mx-0">
                                        Trên hành trình phát triển, Rovi Holdings vinh hạnh liên tiếp được vinh danh tại các giải thưởng và
                                        chứng nhận danh giá trong nước.
                                    </p>
                                </div>

                                {/* Khối nút điều hướng */}
                                <div>
                                    <Link
                                        href="/news?giai-thuong"
                                        className="inline-flex items-center gap-2 bg-green-800 hover:bg-red-600 text-white px-5 py-2 xl:px-4 xl:py-2 2xl:px-5 2xl:py-2.5 text-xs xl:text-[12px] 2xl:text-[14px] font-normal rounded-full transition-colors duration-300 tracking-tight"
                                    >
                                        Xem tất cả giải thưởng
                                        <ChevronRight className="size-2 xl:size-3 2xl:size-4" />
                                    </Link>
                                </div>
                            </div>
                        </div>

                        {/* Khối hiển thị danh sách ảnh giải thưởng */}
                        <div className="grid grid-cols-2 gap-y-6 gap-x-3 w-fit mx-auto xl:w-full xl:mx-0 xl:flex xl:flex-wrap xl:justify-start xl:items-center xl:gap-8 2xl:gap-10">
                            {images.map((imgSrc, index) => (
                                <div key={index} className="relative w-24 h-24 sm:w-28 sm:h-28 xl:w-32 xl:h-auto xl:aspect-square 2xl:w-36 min-[1920px]:w-52">
                                    <Image
                                        src={imgSrc}
                                        alt={`Giải thưởng ${index + 1}`}
                                        fill
                                        className="object-contain"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                </div>
            </div>
        </section>
    )
}
